import { EasterEgg } from "@/components/EasterEgg";
import { AppHeaderBar } from "@/components/common/app-header-bar";
import { IFrameApi } from "@/components/common/iframe-api";
import { MountNotifier } from "@/components/common/mount-notifier";
import { ElementFileUploadContextProvider } from "@/components/common/point-cloud-file-upload-context/element-file-upload-context";
import { RenderingSettingsContextProvider } from "@/components/common/rendering-settings-context";
import { SceneEventsContextProvider } from "@/components/common/scene-events-context";
import { AnalysisFeedbackForm } from "@/components/ui/analysis-feedback-form";
import { AppBanner } from "@/components/ui/app-banner";
import { BackgroundTaskNotifier } from "@/components/ui/background-task-notifier";
import { PublishSuccessDialog } from "@/components/ui/data-management/publish-success-dialog";
import { RegisterSuccessDialog } from "@/components/ui/data-management/register-success-dialog";
import { ModalSpinner } from "@/components/ui/modal-spinner";
import { ModeCanvas } from "@/components/ui/mode-canvas";
import { ProjectOverview } from "@/components/ui/project-overview";
import { TreeContextProvider } from "@/components/ui/tree/tree-context";
import { Unassigned2dPhotosPanel } from "@/components/ui/unassigned-2d-photos-panel";
import { AiChat } from "@/features/ai-chat/ai-chat";
import { selectIsAiChatOpen } from "@/features/ai-chat/ai-selectors";
import { HbToSxgWelcomeDialog } from "@/features/hb-to-sxg-migration/hb-to-sxg-welcome-dialog";
import { NavigationHistoryTracker } from "@/features/navigation-history/use-navigation-history";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useHandleGlobalEscape } from "@/hooks/use-handle-global-escape";
import { getMode } from "@/modes";
import { ModeDataProvider } from "@/modes/mode-data-context";
import { ImportDataDialog } from "@/modes/sheet-mode/import-data/import-data-dialog";
import {
  ImportDataDialogProvider,
  useImportDataDialog,
} from "@/modes/sheet-mode/import-data/import-data-dialog-context";
import { selectEnabledFeatures } from "@/store/features/features-slice";
import { selectModeName } from "@/store/mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { selectActiveTool, selectShowSpinner } from "@/store/ui/ui-selectors";
import { ToolName } from "@/store/ui/ui-slice";
import { CreateReportPanel } from "@/tools/create-report/create-report-panel";
import { DataManagementBaseProviders } from "@faro-lotv/data-management";
import { grey, useBreakpointMdUp } from "@faro-lotv/flat-ui";
import { selectProjectName } from "@faro-lotv/project-source";
import { Stack } from "@mui/material";
import { Activity, Suspense, useCallback, useState, type JSX } from "react";

type ProjectPageProps = {
  /** True to show only the canvas, without the header bar and the side panels */
  canvasOnly?: boolean;
};

/**
 * @returns Loads the project with the provided UI from the API, and then renders the main UI
 */
export function ProjectPage({
  canvasOnly = false,
}: ProjectPageProps): JSX.Element | null {
  // Only load the IFrame API component, if running in an iFrame
  const enableIFrameApi = window.parent !== window.self;

  const viewerFeatures = useAppSelector(selectEnabledFeatures);

  return (
    <TreeContextProvider>
      <ModeDataProvider>
        <ElementFileUploadContextProvider>
          <SceneEventsContextProvider>
            <DataManagementBaseProviders viewerFeatures={viewerFeatures}>
              {enableIFrameApi && <IFrameApi />}
              <RenderingSettingsContextProvider>
                <MainUi canvasOnly={canvasOnly} />
              </RenderingSettingsContextProvider>
            </DataManagementBaseProviders>
          </SceneEventsContextProvider>
        </ElementFileUploadContextProvider>
      </ModeDataProvider>
    </TreeContextProvider>
  );
}

/**
 * @returns Component that renders the ImportDataDialog using the context
 */
function ImportDataDialogRenderer(): JSX.Element | null {
  const { isOpen, closeDialog } = useImportDataDialog();
  return <ImportDataDialog open={isOpen} onClose={closeDialog} />;
}

/**
 * @returns The main UI layout for the app
 */
function MainUi({ canvasOnly }: Required<ProjectPageProps>): JSX.Element {
  const modeName = useAppSelector(selectModeName);
  const mode = getMode(modeName);

  const showAppSpinner = useAppSelector(selectShowSpinner);
  const [isModeUiLoading, setIsModeUiLoading] = useState(false);
  const showSpinner = showAppSpinner || isModeUiLoading;

  const isMdUp = useBreakpointMdUp();

  const projectName = useAppSelector(selectProjectName);
  useDocumentTitle(projectName);

  // analysis user feedback form related state and callback
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState<boolean>(false);
  const closeFeedbackForm = useCallback(() => setIsFeedbackFormOpen(false), []);

  useHandleGlobalEscape();

  const isAiChatOpen = useAppSelector(selectIsAiChatOpen);

  const shouldShowModePanel = useAppSelector((state) =>
    mode.modePanelVisibilitySelector
      ? mode.modePanelVisibilitySelector(state)
      : true,
  );

  const hasPDFReportPanel =
    useAppSelector(selectActiveTool) === ToolName.createPDFReport;
  const hasSnapshotPanel =
    useAppSelector(selectActiveTool) === ToolName.createSnapshot;
  return (
    <ImportDataDialogProvider>
      <NavigationHistoryTracker />
      <ImportDataDialogRenderer />
      <Stack sx={{ height: "100%", width: "100%", overflow: "hidden" }}>
        {!canvasOnly && (
          <>
            <AppHeaderBar
              mode={mode}
              setIsFeedbackFormOpen={setIsFeedbackFormOpen}
            />
            {!mode.exclusive && <AppBanner />}
          </>
        )}
        <RegisterSuccessDialog />
        <PublishSuccessDialog />
        <HbToSxgWelcomeDialog />
        {/* The Suspense is placed as close to the mode UI elements as possible,
          to not suspend the rest of the application while mode data is loading */}
        <Suspense
          fallback={<MountNotifier onMountChanged={setIsModeUiLoading} />}
        >
          <Stack sx={{ flexGrow: 1, flexDirection: "row", overflow: "auto" }}>
            {mode.ModePanel &&
              !canvasOnly &&
              !hasPDFReportPanel &&
              !hasSnapshotPanel &&
              (isMdUp || mode.exclusive) &&
              shouldShowModePanel && (
                <>
                  <ProjectOverview>
                    <mode.ModePanel />
                  </ProjectOverview>
                  <Unassigned2dPhotosPanel />
                </>
              )}
            {/*
              TODO: if we will ever unify sheet/walk/split/overview mode and we could re-use the
              scene/overlay of this unified mode, then the Report "tool" can become a Report "mode"
              and we can remove the lines below by defining CreateReportPanel as its ModePanel
            */}
            {hasPDFReportPanel && !canvasOnly && (
              <ProjectOverview>
                <CreateReportPanel />
              </ProjectOverview>
            )}
            {hasSnapshotPanel && !canvasOnly && (
              <ProjectOverview>
                <CreateReportPanel />
              </ProjectOverview>
            )}
            <Stack direction="row" sx={{ flexGrow: 1, overflow: "auto" }}>
              <ModeCanvas modeName={modeName} />

              <Activity mode={isAiChatOpen ? "visible" : "hidden"}>
                <AiChat />
              </Activity>
            </Stack>
            {!canvasOnly && mode.ModeDrawer && <mode.ModeDrawer />}
            {!canvasOnly && mode.ModeQuickHelpDrawer && (
              <mode.ModeQuickHelpDrawer />
            )}
            <BackgroundTaskNotifier />
          </Stack>
        </Suspense>
      </Stack>
      <ModalSpinner
        sx={{
          color: grey[0],
          // Set the z-index to achieve the following stacking of UI elements:
          //  1) Toasts: snackbar = 1400
          //  2) MainUi > ModalSpinner: snackbar - 1 = 1399
          //  3) Data Management > DataCategoryDialog: snackbar - 2 = 1398
          //  4) ImportDataDialog > Dialog: snackbar - 3 = 1397
          //  5) NewFeaturePopup > FaroPopover: modal + 1 = 1301
          zIndex: (theme) => theme.zIndex.snackbar - 1,
        }}
        open={showSpinner}
      />
      <EasterEgg />
      {isFeedbackFormOpen && (
        <AnalysisFeedbackForm closeDialog={closeFeedbackForm} />
      )}
    </ImportDataDialogProvider>
  );
}
